import { useMemo } from 'react';
import { useReverbPricing } from '../hooks/useReverbPricing';
import {
  getThumbnailDealBadge,
  evaluateMarketTier,
} from '../utils/reverbPricing';

/**
 * Colored glass pill corner badge for browse thumbnails when Reverb price check
 * places the listing at the deep low end of "good value" or in "high".
 *
 * Owner rule 2026-10-09 (supersedes the verdict-only badge gate): ANY fresh
 * v2 comparison band badges — verdicts, single references, and ranges,
 * limited ones included (the DigiTech DL-8 report: $180 against a 2-listing
 * $149–$150 band showed nothing). The comparator stays strict: only a price
 * strictly outside the band badges; inside, endpoints included, never.
 * Old-format/stale data (kind 'legacy') and 'none' never badge.
 */
// Owner display policy (2026-10-09): Good deal / Above market badges are
// REMOVED FOR NOW while the transaction-evidence integration lands — the
// market range plus the single generic caveat is the entire customer-facing
// treatment. The comparator (getThumbnailDealBadge) and its boundary tests
// remain intact; flipping this flag restores the badges unchanged.
const DISPLAY_DEAL_BADGES = false;

const PriceDealThumbnailBadge = ({ make, model, condition, price, decade, listingId }) => {
  const { pricingDoc, loading } = useReverbPricing(make, model, decade);

  const evaluation = useMemo(
    () => evaluateMarketTier(pricingDoc, condition, { excludeListingId: listingId }),
    [pricingDoc, condition, listingId]
  );

  const badge = useMemo(() => {
    // One strict comparator for every fresh band; a single reference is a
    // collapsed band (low === high), so exactly at it shows nothing.
    if (evaluation.kind === 'verdict' || evaluation.kind === 'single' || evaluation.kind === 'range') {
      return getThumbnailDealBadge(evaluation.tier, price);
    }
    return null;
  }, [evaluation, price]);

  if (!DISPLAY_DEAL_BADGES) return null;
  if (loading || !badge) return null;

  const variantClass = badge === 'good' ? 'price-deal-thumbnail-badge--good' : 'price-deal-thumbnail-badge--high';
  const label = badge === 'good' ? 'Good deal' : 'Above market';

  // Owner display policy (2026-10-09): no evidence provenance in any
  // customer-facing title/tooltip/aria — even on this currently disabled
  // path, so re-enabling the badge cannot resurface it. The accessible
  // label is the judgment word alone.
  return (
    <div
      className={`price-deal-thumbnail-badge ${variantClass}`}
      aria-label={label}
    >
      {label}
    </div>
  );
};

export default PriceDealThumbnailBadge;
